import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PRODUCTION_METHOD_TO_QPD } from '@/shared/config/config'
import { useSession } from '@/shared/hooks/useSession'
import { Button, GenericFullscreenDialog } from '@/common'
import { CheckCircleIcon } from '@/common/components/icons'
import { EmissionQualifyingParams } from '@/shared/api/EmissionQualifyingParamsModel'
import {
  AggregatedElectricity,
  MethodAggregatedEmissions,
  useCreateFinalizedReport
} from '@/shared/hooks/api/finalizedReports'
import { FinalizedReportDialogBody } from '@/shared/components'
import {
  DataQualityInfo,
  JustificationInfo,
  QualityAssuranceInfo
} from '@/features/dashboard/pages/finalized-reports/finalized-report-page/constants'
import useI18n from '@/shared/hooks/useI18n'

export default function CreateFinalizeReportDialog({
  isOpen,
  close,
  startDate,
  endDate,
  installationId,
  productionRouteId,
  productionMethodCodes,
  methodAggregatedEmissions,
  aggregatedElectricity
}: {
  isOpen: boolean
  close: () => void
  startDate: string
  endDate: string
  installationId: string
  productionRouteId: string
  productionMethodCodes: string[]
  methodAggregatedEmissions: MethodAggregatedEmissions[]
  aggregatedElectricity: AggregatedElectricity
}) {
  const { t } = useI18n()
  const {
    session: {
      organization: { id: organizationId }
    }
  } = useSession()

  const navigate = useNavigate()

  const qualifyingParametersToFill = useMemo(
    () => productionMethodCodes.map(code => PRODUCTION_METHOD_TO_QPD[code]).flat(),
    [productionMethodCodes]
  )

  const initQualifyingParams = useCallback(() => {
    return qualifyingParametersToFill.reduce((acc, param) => {
      return { ...acc, [param.code]: undefined }
    }, {} as EmissionQualifyingParams)
  }, [qualifyingParametersToFill])

  const [qualifyingParams, setQualifyingParams] =
    useState<EmissionQualifyingParams>(initQualifyingParams())

  const { mutateAsync: createFinalizedReport, isPending: isCreatingFinalizedReport } =
    useCreateFinalizedReport(organizationId, installationId)

  const [state, setState] = useState<{
    dataQualityInfo: DataQualityInfo | null
    qualityAssuranceInfo: QualityAssuranceInfo | null
    defaultValuesJustification: JustificationInfo[] | null
  }>({
    dataQualityInfo: null,
    qualityAssuranceInfo: null,
    defaultValuesJustification: null
  })

  const { dataQualityInfo, qualityAssuranceInfo, defaultValuesJustification } = state

  const isQualifyingParamRequired = useCallback(
    (paramCode: string) => {
      const param = qualifyingParametersToFill.find(param => param.code === paramCode)
      if (!param) {
        return false
      }
      return param.required
    },
    [qualifyingParametersToFill]
  )

  const areAllRequiredQualifyingParamsFilled = qualifyingParametersToFill.every(({ code }) => {
    const isRequired = isQualifyingParamRequired(code)
    return isRequired
      ? qualifyingParams[code as keyof typeof qualifyingParams]?.value !== null
      : true
  })

  const isGeneratePreviewButtonEnabled =
    areAllRequiredQualifyingParamsFilled &&
    dataQualityInfo &&
    qualityAssuranceInfo &&
    dataQualityInfo.length > 0 &&
    qualityAssuranceInfo.length > 0

  const onGeneratePreview = useCallback(async () => {
    if (!isGeneratePreviewButtonEnabled) {
      return
    }

    const { id: finalizedReportId } = await createFinalizedReport({
      startDate,
      endDate,
      productionRouteId,
      dataQualityInfo,
      qualityAssuranceInfo,
      emissionQualifyingParams: qualifyingParams,
      methodAggregatedEmissions: methodAggregatedEmissions,
      aggregatedElectricity,
      installationId,
      defaultValuesJustification
    })

    navigate(
      `/dashboard/${installationId}/production-routes/${productionRouteId}/finalized-reports/${finalizedReportId}`
    )
  }, [
    createFinalizedReport,
    startDate,
    endDate,
    productionRouteId,
    dataQualityInfo,
    defaultValuesJustification,
    qualityAssuranceInfo,
    qualifyingParams,
    methodAggregatedEmissions,
    aggregatedElectricity,
    installationId,
    navigate,
    isGeneratePreviewButtonEnabled
  ])

  const requiredQualifyingParamsCodes = useMemo(
    () =>
      qualifyingParametersToFill
        .filter(param => isQualifyingParamRequired(param.code))
        .map(param => param.code),
    [qualifyingParametersToFill, isQualifyingParamRequired]
  )

  useEffect(() => {
    setQualifyingParams(initQualifyingParams())
  }, [qualifyingParametersToFill, initQualifyingParams])

  const actions = (
    <div className="flex justify-end gap-2">
      <Button variant="neutral" onClick={close}>
        {t('common:actions.cancel')}
      </Button>
      <Button
        isLoading={isCreatingFinalizedReport}
        onClick={onGeneratePreview}
        disabled={!isGeneratePreviewButtonEnabled || isCreatingFinalizedReport}
      >
        {t('reports:finalizedReports.createFinalizeReportDialog.actions.generatePreview')}
      </Button>
    </div>
  )

  return (
    <GenericFullscreenDialog
      title={t('reports:finalizedReports.createFinalizeReportDialog.title')}
      subtitle={t('reports:finalizedReports.createFinalizeReportDialog.subtitle')}
      icon={<CheckCircleIcon className="text-green-600 size-6" />}
      isOpen={isOpen}
      close={close}
      preventCloseOnOutsideClick
      actions={actions}
    >
      <FinalizedReportDialogBody
        productionMethodCodes={productionMethodCodes}
        setQualifyingParams={setQualifyingParams}
        qualifyingParams={qualifyingParams}
        requiredQualifyingParamsCodes={requiredQualifyingParamsCodes}
        state={state}
        updateState={setState}
      />
    </GenericFullscreenDialog>
  )
}
